/**
 * Wrapper for Supabase edge functions with timeout and enhanced error detection
 */

import { SupabaseClient } from '@supabase/supabase-js';

export interface EdgeFunctionOptions {
  timeout?: number; // milliseconds, default 60000 (60s)
  body?: any;
  headers?: Record<string, string>;
}

export interface EdgeFunctionResult<T> {
  data: T | null;
  error: any;
}

/**
 * Invoke a Supabase edge function with timeout and Chrome extension interference detection
 */
export async function invokeWithTimeout<T = any>(
  supabase: SupabaseClient,
  functionName: string,
  options: EdgeFunctionOptions = {}
): Promise<EdgeFunctionResult<T>> {
  const { timeout = 60000, body, headers } = options;
  
  let timeoutId: NodeJS.Timeout | null = null;
  
  // Create a promise that rejects on timeout
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error('TIMEOUT'));
    }, timeout);
  });
  
  try {
    // Race between the actual call and timeout
    const result = await Promise.race([
      supabase.functions.invoke(functionName, { body, headers }),
      timeoutPromise,
    ]) as any;
    
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    
    const { data, error } = result;
    
    // Detect Chrome extension/network interference (including nested Supabase error context)
    if (error) {
      const topMsg = typeof error === 'string' ? error : (error.message || '');
      const nestedMsg = (error?.context?.value?.message || error?.context?.message || error?.context?.text || (error?.cause && (typeof error.cause === 'string' ? error.cause : error.cause.message)) || '') as string;
      
      // Also check stack traces for extension patterns
      const stackTrace = error?.context?.value?.stack || error?.stack || '';
      const combined = `${topMsg} ${nestedMsg} ${stackTrace}`.toLowerCase();

      const isNetworkError = combined.includes('failed to fetch')
        || combined.includes('err_blocked_by_client')
        || combined.includes('networkerror')
        || combined.includes('failed to send a request to the edge function')
        || combined.includes('network request failed')
        || combined.includes('load failed');

      if (isNetworkError && !data) {
        const possibleExtensionInterference = combined.includes('frame_ant.js')
          || combined.includes('content_script.js')
          || combined.includes('extension')
          || combined.includes('chrome-extension')
          || combined.includes('adblock')
          || combined.includes('ublock')
          || combined.includes('privacy badger');

        // Check if it's a CORS error
        const isCorsError = combined.includes('cors') || combined.includes('cross-origin');

        return {
          data: null,
          error: {
            message: possibleExtensionInterference
              ? 'Request blocked - Possible browser extension interference'
              : isCorsError
              ? 'CORS error - Cross-origin request blocked'
              : 'Network error - Request failed',
            details: possibleExtensionInterference
              ? 'An extension may be blocking Supabase Edge Function requests. Try disabling extensions or using an incognito/private window for hjodtxoylcvcwjgvuctk.supabase.co.'
              : isCorsError
              ? `CORS configuration issue with edge function "${functionName}". Please check the function's CORS settings.`
              : `Unable to reach the edge function "${functionName}". Please check your internet connection, verify the function exists and is deployed, and try again.`,
            originalError: topMsg || nestedMsg,
          },
        };
      }
    }
    
    return { data: data as T, error };
  } catch (error: any) {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    
    // Handle timeout
    if (error?.message === 'TIMEOUT' || error?.name === 'AbortError') {
      return {
        data: null,
        error: {
          message: 'Request timeout',
          details: `The request took longer than ${timeout / 1000} seconds and was cancelled. The edge function "${functionName}" may be taking too long to respond.`,
          originalError: 'TIMEOUT',
        },
      };
    }

    // Detect common browser/extension/network blocking patterns
    const rawMessage = (error?.message || error?.toString?.() || '').toString();
    const lower = rawMessage.toLowerCase();
    const blockedByExtension = lower.includes('failed to fetch')
      || lower.includes('err_blocked_by_client')
      || lower.includes('failed to send a request to the edge function')
      || lower.includes('networkerror');

    if (blockedByExtension) {
      return {
        data: null,
        error: {
          message: 'Request blocked - Possible browser extension interference',
          details: 'An extension (ad/tracker blocker) may be blocking Supabase Edge Function requests. Try disabling extensions or using an incognito/private window for hjodtxoylcvcwjgvuctk.supabase.co.',
          originalError: rawMessage,
        },
      };
    }
    
    // Handle other errors
    return {
      data: null,
      error: {
        message: error?.message || 'Unknown error',
        originalError: rawMessage,
      },
    };
  }
}
